import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import theme from 'src/assets/theme/theme';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import { removeDataKeys, removeNullDeep } from 'src/utils/helper';
import { vnFinanzierungDataTransformer } from './vnFinanzierungDataTransformer';

// Schemas
import VerwendungsnachweisSchema from 'src/lib/paths/verwendungsnachweis_FULL_Paths.json';
const VN_P = VerwendungsnachweisSchema.attributes;

const VNFinanzierungsPlanLIS: FC<{}> = () => {
	const { application } = useApplication();
	const [allKostenPositions, setAllKostenPositions] = useState<any[]>([]);

	const allNachweisPosition = useMemo(() => get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []), [application]);

	const data = useMemo(
		() => vnFinanzierungDataTransformer(allNachweisPosition, allKostenPositions, application),
		[allNachweisPosition, allKostenPositions, application]
	);

	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = {
					Foerderantrag: { id: { $eq: get(application, `${VN_P.Foerderantrag.path}.id`) } },
				};
				const res = await GetRecords(APIS.Kostenposition, ['Allgemein_Kosten_Pro_Jahr'], filter);
				if (res) setAllKostenPositions(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err) {
				console.error('ERROR: Fetching KostenPosition ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Mittelabruf' description={PLATZHALTER_TEXT} />
			<Typography variant='body1' sx={{ mb: 2 }}>
				Die Berechnung des Mittelabrufs basiert auf den von Ihnen gemachten Angaben, wird automatisch generiert und steht unter dem Vorbehalt
				einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>

			{data.showWarning && (
				<Box sx={{ mb: 2 }}>
					<MUIAlert
						infoType='warning'
						title='Hinweis zur Förderung'
						message={`Der Betrag des automatisch berechneten Mittelabrufs in Höhe von ${DisplayEuro(data.gesamtMaxFoerderung)} übersteigt die bewilligte Förderung. Bitte beachten Sie, dass maximal die bewilligte Fördersumme abgerufen werden kann.`}
					/>
				</Box>
			)}

			<Box sx={{ mt: '2rem' }}>
				<TableContainer>
					<Table aria-label='Ausgaben- und Finanzierungsplan'>
						<TableHead>
							<TableRow>
								<TableCell></TableCell>
								<TableCell align='right'>Bewilligt</TableCell>
								<TableCell align='right'>Mittelabruf</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.rows.map((row, index) => {
								const isSummary = row.type === 'summary';
								const isQuote = row.type === 'quote';
								const isDetail = row.type === 'detail';
								const isMaxFoerderung = row.type === 'maxFoerderung';

								const isLastThreeRows = index >= data.rows.length - 1;

								return (
									<TableRow
										key={`${row.name}-${index}`}
										sx={isLastThreeRows ? { backgroundColor: theme.palette.background.paper } : undefined}
									>
										<TableCell
											sx={
												isDetail ?
													{ paddingLeft: '5rem', color: theme.palette.text.secondary, fontWeight: isMaxFoerderung ? 600 : 'normal' }
												: isMaxFoerderung || isQuote || isSummary ?
													{ fontWeight: 600 }
												:	{ fontWeight: 600 }
											}
										>
											{row.name}
										</TableCell>
										<TableCell
											align='right'
											sx={{
												fontWeight:
													isMaxFoerderung ? 600
													: isDetail ? 'normal'
													: 600,
											}}
										>
											{row.bewilligt !== null ?
												isQuote ?
													DisplayDecimal(row.bewilligt, true)
												:	DisplayEuro(row.bewilligt)
											:	'-'}
										</TableCell>
										<TableCell
											align='right'
											sx={{
												fontWeight:
													isMaxFoerderung ? 600
													: isDetail ? 'normal'
													: 600,
											}}
										>
											{row.mittelabruf !== null ?
												isQuote ?
													DisplayDecimal(row.mittelabruf, true)
												:	DisplayEuro(row.mittelabruf)
											:	'-'}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
					Ausgaben können nur bis zur im Förderbescheid festgesetzten Ausgabenhöhe als zuwendungsfähig anerkannt werden können. Mögliche
					Mehrausgaben gehen zu Lasten des Zuwendungsempfängers. Dieser Mittelabruf wurde automatisch durch das digitale Antragsportal der
					Innovationsförderung Hessen – basierend auf den von Ihnen gemachten Angaben – erstellt und steht unter dem Vorbehalt einer fachlichen
					Prüfung durch die Hessen Agentur.
				</Typography>
				<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
					<strong>Grundsätzlich gilt:</strong> Der Mittelabruf kann maximal in Höhe der bewilligten Förderung erfolgen. Dabei können
					Netzanschluss und Erdarbeiten mit max. 10.000 EUR pro Standort gefördert werden, Energiespeicher insgesamt mit max. 10.000 EUR.
				</Typography>
			</Box>
		</>
	);
};

export default VNFinanzierungsPlanLIS;
